import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { safeSetInspectionItem } from '../utils/inspectionStorage';
import './DamageReport.css';

function DamageReport({ onLogout }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // Get current inspection ID from URL or localStorage
  const urlParams = new URLSearchParams(window.location.search);
  const inspectionIdFromUrl = urlParams.get('id');
  const sessionId = localStorage.getItem('inspectionSessionId') || 'new';
  // Use URL id if not 'new', otherwise use sessionId
  const storageKey = (inspectionIdFromUrl && inspectionIdFromUrl !== 'new') 
    ? `inspection_${inspectionIdFromUrl}_damage` 
    : `inspection_${sessionId}_damage`;
  const currentInspectionId = inspectionIdFromUrl && !isNaN(Number(inspectionIdFromUrl))
    ? inspectionIdFromUrl
    : null;
  let savedDamageImage = localStorage.getItem(`${storageKey}_image`) || localStorage.getItem(`${storageKey}_image_url`);
  if (savedDamageImage && savedDamageImage.startsWith('/uploads/')) {
    const baseURL = axios.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || '';
    savedDamageImage = `${baseURL}${savedDamageImage}`;
  }

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) {
      return;
    }

    const previous = localStorage.getItem(`${storageKey}_canvas`);
    canvas.width = image.clientWidth;
    canvas.height = image.clientHeight;

    if (previous && previous.startsWith('data:image')) {
      const overlay = new Image();
      overlay.crossOrigin = 'anonymous';
      overlay.onload = () => {
        const ctx = canvas.getContext('2d');
        ctx.drawImage(overlay, 0, 0, canvas.width, canvas.height);
      };
      overlay.src = previous;
    }
  }, [storageKey]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  const pointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] || event;

    return {
      x: source.clientX - rect.left,
      y: source.clientY - rect.top,
    };
  };

  const startDraw = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const point = pointFromEvent(event);

    ctx.strokeStyle = '#ff1f1f';
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    setDrawing(true);
    setSavedMessage('');
  };

  const draw = (event) => {
    if (!drawing) {
      return;
    }

    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const point = pointFromEvent(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDraw = () => {
    setDrawing(false);
  };

  const resetDamage = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    localStorage.removeItem(`${storageKey}_canvas`);
    localStorage.removeItem(`${storageKey}_image`);
    setSavedMessage('');
  };

  const saveDamage = async () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    const composite = document.createElement('canvas');
    composite.width = canvas.width;
    composite.height = canvas.height;

    const ctx = composite.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, composite.width, composite.height);
    ctx.drawImage(image, 0, 0, composite.width, composite.height);
    ctx.drawImage(canvas, 0, 0);

    setSaving(true);
    setSavedMessage('');

    try {
      const token = localStorage.getItem('token');
      const dataUrl = composite.toDataURL('image/jpeg', 0.85);
      const detailsKey = inspectionIdFromUrl && inspectionIdFromUrl !== 'new' ? inspectionIdFromUrl : sessionId;
      const payload = {
        inspectionId: currentInspectionId || null,
        inspectionNumber: localStorage.getItem(`inspection_${detailsKey}_number`) || detailsKey,
        inspectionDate: localStorage.getItem(`inspection_${detailsKey}_date`) || new Date().toISOString().split('T')[0],
        clientId: localStorage.getItem(`inspection_${detailsKey}_clientId`) || null,
        vehicleInfoId: localStorage.getItem(`inspection_${detailsKey}_vehicleId`) || null,
        summary: localStorage.getItem(`inspection_${detailsKey}_summary`) || '',
        damageImage: dataUrl,
      };

      const response = await axios.post('/api/inspections/damage-report', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 120000,
      });

      const savedInspectionId = response.data.inspectionId;
      [
        'number',
        'date',
        'clientId',
        'vehicleId',
        'summary',
        'checklist',
      ].forEach((suffix) => {
        const currentValue = localStorage.getItem(`inspection_${detailsKey}_${suffix}`);
        if (currentValue !== null) {
          safeSetInspectionItem(`inspection_${savedInspectionId}_${suffix}`, currentValue);
        }
      });

      safeSetInspectionItem('inspectionId', response.data.inspectionId);
      safeSetInspectionItem('inspectionSessionId', savedInspectionId);
      safeSetInspectionItem(`inspection_${savedInspectionId}_damage_image`, response.data.damageImage);
      safeSetInspectionItem(`inspection_${savedInspectionId}_damage_canvas`, response.data.damageImage);
      safeSetInspectionItem(`inspection_${savedInspectionId}_damage_image_url`, response.data.damageImage);
      if (!currentInspectionId) {
        safeSetInspectionItem(`inspection_${savedInspectionId}_loaded`, 'true');
      }
      setSavedMessage('Damage report saved.');
      navigate(`/inspection/new?id=${savedInspectionId}`);
    } catch (error) {
      console.error('Error saving damage report:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save damage report.';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="damage-page">
        <header className="damage-header">
          <div className="damage-title-group">
            <button className="app-back-button damage-back" type="button" onClick={() => navigate(`/inspection/new?id=${inspectionIdFromUrl || sessionId}`)} aria-label="Back to inspection">
              Back
            </button>
            <div>
              <h1>Damage report</h1>
              <p>Mark the parts of the vehicle that have damage.</p>
            </div>
          </div>
          <div className="damage-actions">
            <button className="damage-reset" type="button" onClick={resetDamage}>Reset ↻</button>
            <button className="damage-template" type="button">Templates ⧉</button>
          </div>
        </header>

        <section className="damage-canvas-card">
          <div className="damage-canvas-wrap">
            <img ref={imageRef} crossOrigin="anonymous" src={savedDamageImage || "/vehicle-damage-template.png"} alt="Vehicle damage template" onLoad={resizeCanvas} />
            <canvas
              ref={canvasRef}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
          </div>
        </section>

        {savedMessage && <div className="damage-message">{savedMessage}</div>}
        <button className="damage-save" type="button" onClick={saveDamage} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </main>
    </div>
  );
}

export default DamageReport;
